import { z } from 'zod';

import { FannieMaeDelinquencyFileV1 } from '../investorReporting/FannieMae/FannieMaeDelinquencyFile';
import { FHATS264DefaultReasonCode } from '../investorReporting/FHA/FHATS264';
import { FreddieMacEDRV1 } from '../investorReporting/FreddieMac/FreddieMacEDR';

export const QrpcReason = z.enum([
  // Common to both Fannie Mae and Freddie Mac
  'Death of Borrower',
  'Illness of Borrower',
  "Illness of Borrower's Family Member",
  "Death of Borrower's Family Member",
  'Marital Difficulties',
  'Property Problem',
  'Inability to Sell Property',
  'Inability to Rent Property',
  'Military Service',
  'Other',
  'Unemployment',
  'Business Failure',
  'Casualty Loss',
  'Servicing Problems',
  'Payment Adjustment',
  'Payment Dispute',
  'Transfer of Ownership Pending',
  'Fraud',
  'Unable to Contact Borrower',
  'Incarceration',

  // included in Freddie Mac but not Fannie Mae
  'Curtailment of Income',
  'Excessive Obligations',
  'Abandonment of Property',
  'Distant Employment Transfer',
  'Energy‑Environment Cost',
  'National Emergency Declaration',
  'Eligible Disaster Area',

  // FHA-specific — NEW Aug 2025 (disaster-related DDR codes)
  'Disaster Damaged Property',
  'Income Loss Due to Disaster',
  'Unable to Contact Borrower Disaster',
]);

export type QrpcReason = z.infer<typeof QrpcReason>;

export const QrpcReasonToFannieMae: Record<QrpcReason, FannieMaeDelinquencyFileV1['reasonForDelinquencyCode']> = {
  'Death of Borrower': '001',
  'Illness of Borrower': '002',
  "Illness of Borrower's Family Member": '003',
  "Death of Borrower's Family Member": '004',
  'Marital Difficulties': '005',
  'Property Problem': '011',
  'Inability to Sell Property': '012',
  'Inability to Rent Property': '013',
  'Military Service': '014',
  Other: '015',
  Unemployment: '016',
  'Business Failure': '017',
  'Casualty Loss': '019',
  'Servicing Problems': '023',
  'Payment Adjustment': '026',
  'Payment Dispute': '027',
  'Transfer of Ownership Pending': '029',
  Fraud: '030',
  'Unable to Contact Borrower': '031',
  Incarceration: 'INC',

  // included in Freddie Mac but not Fannie Mae so we map to "Other"
  // TODO review these
  'Curtailment of Income': '015',
  'Excessive Obligations': '015',
  'Abandonment of Property': '015',
  'Distant Employment Transfer': '015',
  'Energy‑Environment Cost': '015',
  'National Emergency Declaration': '015',
  'Eligible Disaster Area': '015',

  // FHA-specific (no Fannie Mae mapping — map to "Other")
  'Disaster Damaged Property': '015',
  'Income Loss Due to Disaster': '015',
  'Unable to Contact Borrower Disaster': '015',
};

export const QrpcReasonToFreddieMacEDR: Record<QrpcReason, FreddieMacEDRV1['defaultReasonCode']> = {
  'Death of Borrower': '001',
  'Illness of Borrower': '002',
  "Illness of Borrower's Family Member": '003',
  "Death of Borrower's Family Member": '004',
  'Marital Difficulties': '005',
  'Curtailment of Income': '006',
  'Excessive Obligations': '007',
  'Abandonment of Property': '008',
  'Distant Employment Transfer': '009',
  'Property Problem': '011',
  'Inability to Sell Property': '012',
  'Inability to Rent Property': '013',
  'Military Service': '014',
  Other: '015',
  Unemployment: '016',
  'Business Failure': '017',
  'Casualty Loss': '019',
  'Energy‑Environment Cost': '022',
  'Servicing Problems': '023',
  'Payment Adjustment': '026',
  'Payment Dispute': '027',
  'Transfer of Ownership Pending': '029',
  Fraud: '030',
  'Unable to Contact Borrower': '031',
  'National Emergency Declaration': '032',
  'Eligible Disaster Area': '034',
  Incarceration: 'INC',

  // FHA-specific (no Freddie Mac mapping — map to "Other")
  'Disaster Damaged Property': '015',
  'Income Loss Due to Disaster': '015',
  'Unable to Contact Borrower Disaster': '015',
};

/**
 * Map from QRPC default reason to the corresponding FHA TS264 DDR (Default Reason) code.
 * Very similar to the Freddie Mac mapping, with the exception that National Emergency
 * Declaration maps to '055' for FHA (vs '032' for Freddie Mac).
 */
export const QrpcReasonToFHATS264: Record<QrpcReason, FHATS264DefaultReasonCode> = {
  'Death of Borrower': '001',
  'Illness of Borrower': '002',
  "Illness of Borrower's Family Member": '003',
  "Death of Borrower's Family Member": '004',
  'Marital Difficulties': '005',
  'Curtailment of Income': '006',
  'Excessive Obligations': '007',
  'Abandonment of Property': '008',
  'Distant Employment Transfer': '009',
  'Property Problem': '011',
  'Inability to Sell Property': '012',
  'Inability to Rent Property': '013',
  'Military Service': '014',
  Other: '015',
  Unemployment: '016',
  'Business Failure': '017',
  'Casualty Loss': '019',
  'Energy‑Environment Cost': '022',
  'Servicing Problems': '023',
  'Payment Adjustment': '026',
  'Payment Dispute': '027',
  'Transfer of Ownership Pending': '029',
  Fraud: '030',
  'Unable to Contact Borrower': '031',
  'National Emergency Declaration': '055',
  'Eligible Disaster Area': '055',
  Incarceration: 'INC',

  // NEW Aug 2025 — Disaster-related DDR codes
  'Disaster Damaged Property': 'A43',
  'Income Loss Due to Disaster': 'A45',
  'Unable to Contact Borrower Disaster': 'A46',
};
